/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Button from '@react/react-spectrum/Button';
import I18nProvider, {I18nContext} from '@adobe/adobe-intl-react';
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage';
import * as localeData from './__localization__/NotFound';
import React from 'react';

function NotFound({timeout}) {
  const {formatMessage, locale} = React.useContext(I18nContext);
  return (
    <I18nProvider locale={locale} localeData={localeData}>
      <Illustration formatMessage={formatMessage} timeout={timeout} />
    </I18nProvider>
  );
}

function Illustration({formatMessage, timeout}) {
  const description = (
    <div className="errorPage-description-container">
      <p>
        {timeout ?
          formatMessage({
            defaultMessage: 'This page was not able to load. Try refreshing or visit a different page.',
            messageId: 'refreshDescription'
          }) :
          formatMessage({
            defaultMessage: 'This page isn\'t available. Try checking the URL or visit a different page.',
            messageId: 'noAccessDescription'
          })
        }
      </p>
      {timeout &&
        <Button
          element="button"
          label="Refresh"
          onClick={() => window.location.reload()}
          style={{marginTop: '10px'}}
          variant="cta"
        />
      }
    </div>
  );

  const heading = timeout ?
    formatMessage({
      defaultMessage: 'Error 408: Request timeout',
      messageId: 'refreshHeading'
    }) :
    formatMessage({
      defaultMessage: 'Error 404: Page not found',
      messageId: 'noAccessHeading'
    });

  React.useEffect(() => {
    const divider = ' | ';
    const parts = document.title.split(divider);
    document.title = heading + divider + parts[parts.length - 1];
  });

  const Svg404 = (
    <svg height="110" viewBox="536.879 -2419.5 135.321 87">
      <g fill="none" stroke="#b3b3b3" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" transform="translate(1161.9 84)">
        <path d="M807.8,60.5v23a2.006,2.006,0,0,0,2,2h118a2.006,2.006,0,0,0,2-2V3.5a2.006,2.006,0,0,0-2-2h-118a2.006,2.006,0,0,0-2,2v27" strokeWidth="3px" transform="translate(-1421 -2503.5)" />
        <line strokeWidth="3px" transform="translate(-622.9 -2467.6)" x2="19.3" y1="19.2" />
        <line strokeWidth="3px" transform="translate(-622.9 -2467.6)" x1="19.3" y1="19.2" />
        <line strokeWidth="2px" transform="translate(-613.3 -2489.5)" x1="122" />
        <line strokeWidth="2px" transform="translate(-605.3 -2495.5)" x1="10" />
      </g>
    </svg>
  );

  return (
    <IllustratedMessage
      className={timeout && 'errorPage-illustratedMessage'}
      description={description}
      heading={heading}
      illustration={Svg404}
    />
  );
}

export default React.memo(NotFound);
